import React, { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { errorTypes } from '@/utility/validation'
import { useFormContext } from 'react-hook-form'
import SearchSelect from '@/components/common/SearchSelect'
import { SiteService } from '@/services/Site/SiteServive'
import ShiftAndTimeService from '@/services/ShiftAndTime/ShiftAndTime'
import { DesignationService } from '@/services/Designation/DesignationService'
import { DepartmentService } from '@/services/Department/Department.ts'
import { ThrowError } from '@/api/callApi'
import type { OnboardTabProps } from './onboard.types'

const OnboardPermissions: React.FC<OnboardTabProps> = ({ isLoading, handleNextStep }) => {
    const methods = useFormContext()
    const { setValue, watch, register, formState: { errors } } = methods
    const [sites, setSites] = useState<{ value: string; label: string }[]>([])
    const [shifts, setShifts] = useState<{ value: string; label: string }[]>([])
    const [designations, setDesignations] = useState<{ value: string; label: string }[]>([])
    const [departments, setDepartments] = useState<{ value: string; label: string }[]>([])

    const fetchData = async () => {
        try {
            const [siteRes, shiftRes, desRes, depRes] = await Promise.all([
                SiteService.getSite(),
                ShiftAndTimeService.getShiftAndTime(),
                DesignationService.getDesignation(),
                DepartmentService.getDepartment()
            ])

            if (siteRes?.status) {
                setSites((siteRes?.data?.site || []).map(s => ({ value: s.id!, label: s.name! })))
            }
            if (shiftRes?.status) {
                setShifts((shiftRes?.data?.shift || []).map(s => ({ value: s.id, label: s.shiftName })))
            }
            if (desRes?.status) {
                setDesignations((desRes?.data?.designation || []).map(d => ({ value: d.id!, label: d.name! })))
            }
            if (depRes?.status) {
                setDepartments((depRes?.data?.department || []).map(d => ({ value: d.id!, label: d.name! })))
            }
        } catch (error) {
            ThrowError(error)
        }
    }

    useEffect(() => {
        fetchData()
        register("siteId", { ...errorTypes.required("Site") })
        register("shiftId", { ...errorTypes.required("Shift") })
        register("designationId", { ...errorTypes.required("Designation") })
        register("departmentId", { ...errorTypes.required("Department") })
    }, [register])

    return (
        <div className='space-y-6'>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SearchSelect
                    label="Site"
                    required
                    placeholder="Select Site"
                    options={sites}
                    value={watch("siteId")}
                    onChange={(val) => setValue("siteId", val, { shouldValidate: true })}
                    error={errors.siteId?.message as string}
                />
                <SearchSelect
                    label="Shift"
                    required
                    placeholder="Select Shift"
                    options={shifts}
                    value={watch("shiftId")}
                    onChange={(val) => setValue("shiftId", val, { shouldValidate: true })}
                    error={errors.shiftId?.message as string}
                />
                <SearchSelect
                    label="Department"
                    required
                    placeholder="Select Department"
                    options={departments}
                    value={watch("departmentId")}
                    onChange={(val) => setValue("departmentId", val, { shouldValidate: true })}
                    error={errors.departmentId?.message as string}
                />
                <SearchSelect
                    label="Designation"
                    required
                    placeholder="Select Designation"
                    options={designations}
                    value={watch("designationId")}
                    onChange={(val) => setValue("designationId", val, { shouldValidate: true })}
                    error={errors.designationId?.message as string}
                />
            </div>
            <div className="flex items-center justify-end mt-30">
                <Button
                    type="button"
                    disabled={isLoading}
                    onClick={() => handleNextStep('tab3', ['siteId', 'shiftId', 'designationId', 'departmentId'])}
                >
                    {isLoading ? 'Submitting...' : 'Update and Continue'}
                </Button>
            </div>
        </div>
    )
}

export default OnboardPermissions
